import { createActorContext } from '@xstate/react';
import { useRouter } from 'next/router';
import { type ReactNode } from 'react';
import { useNavigationContext } from '../../../../components/OnboardingVision/NavigationProvider';
import {
  type FlowContext as FlowMachineContext,
  flowMachine,
} from './flow-machine';
import { getStepPath } from './utils';

export const FlowContext = createActorContext(flowMachine);

export const FlowProvider = ({
  children,
  customContext,
}: {
  children: ReactNode;
  customContext?: FlowMachineContext;
}) => {
  const { onBackNavigation, onForwardNavigation } = useNavigationContext();
  const router = useRouter();

  const machineWithConfig = flowMachine.withConfig(
    {
      services: {
        handleFlowNavigation: async (
          { flow, previousStepPath, nextStepPath },
          event,
        ) => {
          if (event.type === 'BACK' && previousStepPath) {
            onBackNavigation();
            router.push(previousStepPath, undefined, { shallow: true });
          }

          if (event.type === 'NEXT' && nextStepPath) {
            onForwardNavigation();
            router.push(nextStepPath, undefined, { shallow: true });
          }

          if (event.type === 'PAYMENT_ERROR_REDIRECT') {
            const path = getStepPath({ flow, identifier: 'payment' });

            onBackNavigation();

            // Do a router.replace instead of push to avoid finalize as a new entry in the history stack
            // Await for the replace to complete to prevent the machine from eagerly triggering the UPDATE_FLOW_STEPS event
            await router.replace(path, undefined, { shallow: true });
          }

          return event.type;
        },
      },
    },
    customContext,
  );

  return (
    <FlowContext.Provider machine={() => machineWithConfig}>
      {children}
    </FlowContext.Provider>
  );
};
